import {CatalogSuggestionGroups} from "src/app/modules/shared/interfaces/CatalogSuggestionGroups.interface";
import {WorkEntitySuggestion} from "src/app/modules/shared/interfaces/WorkEntitySuggestion.interface";

const EXACT_MATCH = 0;
const WORD_MATCH = 1;
const PREFIX_MATCH = 2;
const OTHER_MATCH = 3;

export function buildCatalogSuggestionGroups(
  query: string,
  people: readonly WorkEntitySuggestion[],
  collections: readonly WorkEntitySuggestion[]
): CatalogSuggestionGroups {
  const queryWords = getNameWords(query);
  if (queryWords.length === 0)
    return {people: [], collections: [], collectionsFirst: false};

  const ranks = new Map(
    [...people, ...collections].map((entity) => [
      entity,
      getMatchRank(entity, queryWords),
    ])
  );
  const bestRank = Math.min(...ranks.values());
  // Complete words disambiguate Persona from Personal while prefixes remain useful during typing.
  const maximumRank = bestRank <= WORD_MATCH ? WORD_MATCH : PREFIX_MATCH;
  const visiblePeople = rankSuggestions(people, ranks, maximumRank);
  const visibleCollections = rankSuggestions(collections, ranks, maximumRank);
  return {
    people: visiblePeople,
    collections: visibleCollections,
    collectionsFirst:
      (ranks.get(visibleCollections[0]) ?? OTHER_MATCH) <
      (ranks.get(visiblePeople[0]) ?? OTHER_MATCH),
  };
}

function rankSuggestions(
  entities: readonly WorkEntitySuggestion[],
  ranks: ReadonlyMap<WorkEntitySuggestion, number>,
  maximumRank: number
): WorkEntitySuggestion[] {
  return entities
    .filter((entity) => (ranks.get(entity) ?? OTHER_MATCH) <= maximumRank)
    .sort(
      (first, second) =>
        (ranks.get(first) ?? OTHER_MATCH) - (ranks.get(second) ?? OTHER_MATCH)
    );
}

function getMatchRank(
  entity: WorkEntitySuggestion,
  queryWords: readonly string[]
): number {
  const nameWords = getNameWords(entity.name);
  if (nameWords.join(" ") === queryWords.join(" ")) return EXACT_MATCH;
  if (queryWords.every((word) => nameWords.includes(word))) return WORD_MATCH;
  if (
    queryWords.every((word) => nameWords.some((name) => name.startsWith(word)))
  )
    return PREFIX_MATCH;
  return OTHER_MATCH;
}

function getNameWords(value: string): string[] {
  return (
    value
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .match(/[\p{L}\p{N}]+/gu) ?? []
  );
}
